import useArrayState from "@clave/use-array-state";
import React, { useEffect, useState, useMemo } from "react";
import auctionApiClient from "../auctions-list-page/AuctionApiClient";
import { AuctionClassTypes, } from "../auctions-list-page/contracts";
import { getTimeRemainingSecond } from "../components/auctionLots/auctionBidTimeFormat";
import getAuctionHub from "../getAuctionHub";
import { formatDate, formatDateTime } from "../utils/formatDate";
import { formatPriceWs } from "../utils/formatPrice";
import styles from "./OutBidNotification.module.scss";
import config from "../utils/config";
const TimerHoursTreshold = 24;
// Render OutBid Notification on onOutBid signal (when the another user outbids our offer).
// Caution! onOutBid signal is being resent on hub connected event.
// Waiting for a closing notifications are stored inside durable function, so we can read them all and render.
function NotificationItem({ item, strings, onClose, onNavigateToAuction, }) {
    const [render, setRender] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState("");
    const imageUrl = useMemo(() => `${config.auctionItemsImagesRootUrl}/img/${item.id}/${item.mainImageFileId}`, [item]);
    useEffect(() => {
        getTimeRemainingText(item).then(setTimeRemaining);
    }, [item.endAt]);
    function formatEndDate(auctionItem) {
        var _a, _b;
        return auctionItem.auctionType == AuctionClassTypes.COMBINED.toString()
            ? formatDate((_a = auctionItem === null || auctionItem === void 0 ? void 0 : auctionItem.endAt) !== null && _a !== void 0 ? _a : "")
            : formatDateTime((_b = auctionItem === null || auctionItem === void 0 ? void 0 : auctionItem.endAt) !== null && _b !== void 0 ? _b : "");
    }
    function fadeOut() {
        setRender(false);
        setTimeout(onClose, 1000);
    }
    useEffect(() => {
        setRender(true);
        setTimeout(fadeOut, 10000);
    }, []);
    const animationStyle = render ? styles.show : styles.hide;
    return (React.createElement("div", { className: `${styles.content} ${animationStyle}` },
        React.createElement("button", { className: styles.closeBtn, onClick: onClose },
            React.createElement("i", { className: "icomoon-cross" })),
        React.createElement("div", { className: styles.title },
            React.createElement("h3", null, strings.title)),
        React.createElement("div", { className: styles.image },
            React.createElement("img", { src: imageUrl, alt: `Lot ${item.number}` })),
        React.createElement("div", { className: styles.description },
            React.createElement("span", null,
                strings.objectNumber,
                ": ",
                item.number),
            timeRemaining && (React.createElement("span", null,
                strings.timeRemaining,
                ": ",
                timeRemaining)),
            !timeRemaining && (React.createElement("span", null,
                strings.endAtLabel,
                ": ",
                formatEndDate(item))),
            React.createElement("div", { className: styles.currentBidPrice },
                React.createElement("span", null,
                    strings.currentBidPriceLabel,
                    ":",
                    " ",
                    React.createElement("span", { className: styles.highlight }, formatPriceWs(item.highestBidAmount))))),
        React.createElement("div", { className: styles.bidBtn },
            React.createElement("a", { onClick: onNavigateToAuction }, strings.bidBtnLabel))));
}
export function OutBidNotification({ auctionUrl, strings }) {
    const [outBidItems, setOutBidItems] = useArrayState([]);
    async function onOutBid(notification) {
        // Get item from the API
        const item = await auctionApiClient.getItem(notification.lotId);
        // Construct notification item
        let auctionLotNotificationItem = {
            ...item,
            notificationId: notification.id,
            highestBidAmount: notification.highestBidAmount,
        };
        // If auction item is not available remove it instead of displaying
        if (!item.isAvailable) {
            await removeNotification(auctionLotNotificationItem);
        }
        // Otherwise, display notification
        else {
            setOutBidItems.prepend(auctionLotNotificationItem);
        }
    }
    async function closeNotification(item) {
        const index = outBidItems.findIndex((itm) => itm.notificationId === item.notificationId);
        if (index > -1) {
            await removeNotification(item);
            setOutBidItems.removeAt(index);
        }
    }
    async function navigateToAuction(item, auctionUrl) {
        await removeNotification(item);
        window.location.href = `${auctionUrl}${item.relativeUrl}`;
    }
    async function removeNotification(item) {
        const hub = await getAuctionHub();
        await (hub === null || hub === void 0 ? void 0 : hub.notificationAck(item.notificationId));
    }
    useEffect(() => {
        getAuctionHub().then((hub) => {
            hub === null || hub === void 0 ? void 0 : hub.onOutBid(onOutBid);
        });
    }, []);
    return (React.createElement(React.Fragment, null, outBidItems.length > 0 && (React.createElement("div", { className: styles.wrapper }, outBidItems.map((item) => (React.createElement(NotificationItem, { key: item.notificationId, item: item, strings: strings, onNavigateToAuction: () => navigateToAuction(item, auctionUrl), onClose: () => closeNotification(item) })))))));
}
async function getTimeRemainingText(item) {
    var _a, _b;
    const endDate = new Date((_a = item.endAt) !== null && _a !== void 0 ? _a : 0);
    const hours = Math.abs(endDate.getTime() - Date.now()) / 1000 / 3600;
    if (hours > TimerHoursTreshold)
        return "";
    //For EVT type 'COMBINED' endDate's time doesn't reflect actual ending and shouldn't be displayed with that precision
    if (item.auctionType == AuctionClassTypes.COMBINED)
        return "";
    const timeCounter = getTimeRemainingSecond((_b = item.secondsTillEnd) !== null && _b !== void 0 ? _b : 0);
    return timeCounter
        .splice(0, timeCounter.length - 1) // Do not show seconds
        .join(", ");
}
